import {
  Box,
  Rating,
  TextField,
  Typography,
  Button,
  CircularProgress,
  Alert,
  AlertTitle,
} from "@mui/material";
import { addDoc, collection } from "firebase/firestore";
import React, { Fragment, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Slider from "react-slick";
import { db } from "../firebase";

const TestimonialSlider = ({ reviews }) => {
  const [reviewContent, setReviewContent] = useState("");
  const [reviewName, setReviewName] = useState("");
  const [reviewRating, setReviewRating] = useState(5);
  const { t } = useTranslation();
  const [formState, setFormState] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let timer;
    if (formState === "success") {
      timer = setTimeout(() => {
        setFormState("idle");
        setReviewContent("");
        setReviewName("");
        setReviewRating(5);
      }, 8000);
    }
    return () => clearTimeout(timer);
  }, [formState]);

  const handleAddReview = async () => {
    setFormState("idle");
    setErrorMessage("");

    if (!reviewContent || !reviewName || reviewRating === null) {
      setErrorMessage(t("reviewsSection.allFieldsRequired"));
      setFormState("error");
      return;
    }

    setFormState("submitting");

    try {
      await addDoc(collection(db, "patientReviews"), {
        description: reviewContent,
        name: reviewName,
        ratings: reviewRating,
        createdAt: new Date(),
        isVisible: false,
      });

      setFormState("success");
    } catch (error) {
      console.error("Error adding review:", error);
      setErrorMessage(t("reviewsSection.errorAddingReview"));
      setFormState("error");
    }
  };

  const getButtonContent = () => {
    switch (formState) {
      case "submitting":
        return (
          <Box display="flex" alignItems="center" justifyContent="center">
            <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
            <span>{t("reviewsSection.submitting") || "Submitting..."}</span>
          </Box>
        );
      // case "success":
      //   return t("reviewsSection.submitted") || "Submitted!";
      default:
        return t("reviewsSection.submit") || "Submit";
    }
  };

  const getButtonColor = () => {
    switch (formState) {
      case "success":
        return "green";
      case "error":
        return "#d32f2f";
      default:
        return "var(--color-fourth)";
    }
  };

  return (
    <Fragment>
      <div
        className="col-lg-9 col-md-10"
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <div style={{ padding: "50px" }}>
          <Slider
            infinite={reviews.length > 1}
            dots={true}
            arrows={true}
            speed={500}
            slidesToShow={reviews.length > 1 ? 1 : 1}
            slidesToScroll={1}
            autoplay={true}
            autoplaySpeed={5000}
            className="content-wrapper"
          >
            {reviews.map((review, index) => (
              <div key={index} className="single-item-wrap">
                <div
                  className="review-rating"
                  style={{
                    width: "100%",
                    justifyContent: "center",
                    display: "flex",
                  }}
                >
                  <Rating
                    name="read-only"
                    value={review.ratings}
                    readOnly
                    sx={{
                      "& .MuiRating-icon": {
                        fontSize: "45px",
                      },
                    }}
                  />
                </div>

                <p className="content" style={{ fontSize: "18px" }}>
                  "{review.description}"
                </p>

                <div className="author-info">
                  <h5 className="name" style={{ fontSize: "14px" }}>
                    -{review.name}
                  </h5>
                </div>
              </div>
            ))}
          </Slider>
        </div>

        {/* Review form */}
        <div
          className="add-review-form"
          style={{
            display: "flex",
            flexDirection: "column",
            borderColor: "black",
            borderTop: "2px solid white",
            borderBottom: "2px solid white",
            justifyContent: "center",
            marginLeft: "40px",
            marginRight: "40px",
            marginTop: "100px",
            padding: "40px",
            borderRadius: "18px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
            backgroundColor: "white",
          }}
        >
          <Typography
            variant="h6"
            className="form-title"
            style={{
              marginBottom: "15px",
              color: "var(--color-fourth)",
              textAlign: "center",
              fontWeight: "bold",
              fontSize: "24px",
            }}
          >
            <div>{t("reviewsSection.title") || "Leave a Review"}</div>
          </Typography>

          {/* Form state notification */}
          {formState === "success" && (
            <Alert
              severity="success"
              sx={{
                mb: 3,
                fontWeight: "bold",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                animation: "fadeInAnimation 0.3s ease-in-out forwards",
              }}
            >
              <AlertTitle>Success!</AlertTitle>
              {t("reviewsSection.reviewAdded") ||
                "Your review has been successfully submitted. Thank you!"}
            </Alert>
          )}

          {formState === "error" && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                animation: "fadeInAnimation 0.3s ease-in-out forwards",
              }}
            >
              <AlertTitle>Error</AlertTitle>
              {errorMessage}
            </Alert>
          )}

          <Box className="rating-box mb-3">
            <Typography
              style={{
                marginBottom: "20px",
                color: "var(--color-fourth)",
                fontWeight: "bold",
              }}
            >
              {t("reviewsSection.ratings") || "Your Rating"}:
            </Typography>
            <Rating
              name="simple-controlled"
              value={reviewRating}
              sx={{
                "& .MuiRating-icon": {
                  fontSize: "32px",
                },
              }}
              onChange={(event, newValue) => setReviewRating(newValue)}
              disabled={formState === "submitting" || formState === "success"}
            />
          </Box>

          <TextField
            label={t("reviewsSection.description") || "Your Review"}
            variant="outlined"
            multiline
            rows={2}
            fullWidth
            value={reviewContent}
            onChange={(e) => setReviewContent(e.target.value)}
            InputProps={{
              style: {
                borderRadius: "6px",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                color: "var(--color-fourth)",
              },
            }}
            InputLabelProps={{
              style: {
                color: "var(--color-fourth)",
                padding: "0 4px",
              },
            }}
            sx={{
              "& .MuiOutlinedInput-notchedOutline": {
                border: "1px solid #ccc",
              },
              "& .MuiInputLabel-shrink": {
                transform: "translate(14px, -6px) scale(0.75)",
                padding: "0 4px",
              },
            }}
            style={{
              marginBottom: "10px",
              color: "var(--color-fourth)",
            }}
            className="mb-3"
            disabled={formState === "submitting" || formState === "success"}
          />

          <TextField
            label={t("reviewsSection.name") || "Your Name"}
            variant="outlined"
            fullWidth
            value={reviewName}
            onChange={(e) => setReviewName(e.target.value)}
            className="mb-3"
            InputProps={{
              style: {
                borderRadius: "6px",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
              },
            }}
            InputLabelProps={{
              style: {
                color: "var(--color-fourth)",
                padding: "0 4px",
              },
            }}
            sx={{
              "& .MuiOutlinedInput-notchedOutline": {
                border: "1px solid #ccc",
              },
              "& .MuiInputLabel-shrink": {
                transform: "translate(14px, -6px) scale(0.75)",
                padding: "0 4px",
              },
            }}
            style={{
              marginBottom: "10px",
            }}
            disabled={formState === "submitting" || formState === "success"}
          />

          <Box display="flex" flexDirection="column" alignItems="center">
            <Button
              variant="contained"
              onClick={handleAddReview}
              style={{
                width: "50%",
                textAlign: "center",
                margin: "auto",
                marginTop: "15px",
                padding: "10px 20px",
                fontSize: "14px",
                borderRadius: "6px",
                fontWeight: "bold",
                textTransform: "capitalize",
                backgroundColor: getButtonColor(),
                color: "white",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                transition: "background-color 0.3s ease",
              }}
              disabled={formState === "submitting" || formState === "success"}
            >
              {getButtonContent()}
            </Button>
          </Box>
        </div>
      </div>

      {/* Add a global style for animation */}
      <style jsx global>{`
        @keyframes fadeInAnimation {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </Fragment>
  );
};

export default TestimonialSlider;
